import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { reverse } from "@reactivated";

import bestMatchIcon from "../../../../img/icons/mattress-match/first-match.svg";
import secondMatchIcon from "../../../../img/icons/mattress-match/second-match.svg";
import { ConfiguratorFinancing } from "../../../components/ConfiguratorFinancing";
import { ConfiguratorProductPrice } from "../../../components/ConfiguratorProductPrice";
import { Image } from "../../../components/Image";
import { Link } from "../../../components/Link";
import { RichText } from "../../../components/RichText";
import { strings } from "../../../localization";
import { IProduct } from "../../../models/catalogue";
import { DiscountDisplayMode } from "../../../models/discountDisplay";
import { isoWebPageURL } from "../../../models/nominals";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import { addProductToBasket } from "../../../utils/basket";
import { getOptionValueDisplayName } from "../../../utils/catalogue";
import { navigateToURL } from "../../../utils/urls";
import { ValueProps } from "../ValueProps";

import styles from "./index.module.scss";

export const Result = (
    props: Immutable<{
        product: IProduct;
        productImg: string;
        productCopy: string;
        isBestMatch: boolean;
        feel: {
            icon_url: string | null;
            name: string;
        } | null;
    }>,
) => {
    const onAddToBasket = async (
        event: React.MouseEvent<HTMLElement>,
        product: IProduct,
    ) => {
        event.preventDefault();
        try {
            await addProductToBasket(product.url);
            trackAddToBasketEvent(product);
            navigateToURL(isoWebPageURL.wrap(reverse("basket:summary")));
        } catch (e) {
            return;
        }
    };

    const attrs = props.product?.attributes;

    return (
        <div className={styles.result}>
            <div className={styles.headerContainer}>
                <SVG
                    src={props.isBestMatch ? bestMatchIcon : secondMatchIcon}
                />
                <div className={styles.header}>
                    <h2 className={styles.productTitle}>
                        {props.product.parent?.title}
                    </h2>
                    <div className={styles.feelContainer}>
                        <div className={styles.iconContainer}>
                            {props.feel?.icon_url && (
                                <Image
                                    data-src={props.feel.icon_url}
                                    alt={props.feel.name}
                                />
                            )}
                        </div>
                        <p className={styles.productFeel}>
                            {attrs.option_feel
                                ? getOptionValueDisplayName(attrs.option_feel)
                                : null}
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.productContainer}>
                <div className={styles.productInfo}>
                    <div className={styles.imageContainer}>
                        <Image
                            data-src={props.productImg}
                            className={styles.productImage}
                            alt=""
                            lazy={true}
                        />
                    </div>
                    <div className={styles.priceWrapper}>
                        <div className={styles.priceContent}>
                            <div>
                                <ConfiguratorProductPrice
                                    price={props.product?.price}
                                    strikeThroughMSRP={false}
                                    discountDisplayMode={
                                        DiscountDisplayMode.SHOW_ABSOLUTE
                                    }
                                    actualPriceStyle={"match"}
                                ></ConfiguratorProductPrice>
                                <span className={styles.sizeDisplay}>
                                    {props.product.attributes.option_size
                                        ? getOptionValueDisplayName(
                                              props.product.attributes
                                                  .option_size,
                                          )
                                        : null}
                                </span>
                            </div>
                            <ConfiguratorFinancing
                                price={props.product?.price}
                                variant={props.product}
                            ></ConfiguratorFinancing>
                        </div>
                    </div>
                    <Link
                        className={styles.detailsButton}
                        target="_top"
                        href={props.product.link}
                    >
                        {t`Product Details`}
                    </Link>
                    <button
                        className={styles.addButton}
                        onClick={(e) => {
                            onAddToBasket(e, props.product);
                        }}
                    >
                        {t`Add to Cart`}
                    </button>
                    {props.product.value_props && <ValueProps />}
                </div>
                <div className={styles.optionContainer}>
                    <h1>{strings.get("MATTRESS_SELECTOR_RESULTS_HEADING")}</h1>
                    <RichText html={props.productCopy} />
                </div>
            </div>
        </div>
    );
};
